import { useState } from 'react'
import {
  Box, Typography, Button, Avatar, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Alert, Card,
} from '@mui/material'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { Add, AdminPanelSettings, Shield } from '@mui/icons-material'
import { usePlatformAdmins, useCreatePlatformAdmin } from '../../hooks/usePlatformAdmin'
import { adminGridSx } from '../../layouts/AdminLayout'
import { format } from 'date-fns'
import { isPasswordValid, passwordError } from '../../lib/password'
import { AdminDataError } from '../../components/AdminDataError'
import { AdminPageHeader } from '../../components/AdminPageHeader'
import { AdminEmptyState } from '../../components/AdminEmptyState'

const emptyForm = { name: '', email: '', password: '' }

export default function AdminAdminsPage() {
  const adminsQuery = usePlatformAdmins()
  const { data: admins = [], isLoading } = adminsQuery
  const createAdmin = useCreatePlatformAdmin()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [showPassword, setShowPassword] = useState(false)

  const handleSubmit = async () => {
    try {
      await createAdmin.mutateAsync({ ...form, name: form.name.trim(), email: form.email.trim() })
      setDialogOpen(false)
      setForm(emptyForm)
    } catch { /* API interceptor shows the failure */ }
  }

  const columns: GridColDef[] = [
    {
      field: 'name', headerName: 'Administrator', flex: 1, minWidth: 200,
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{
            width: 30, height: 30, fontSize: 12, fontWeight: 700,
            background: 'linear-gradient(135deg, #34A8E0, #2B8BB9)',
          }}>
            {row.name?.[0]?.toUpperCase() ?? 'A'}
          </Avatar>
          <Typography variant="body2" color="#1e293b" fontWeight={500}>{row.name}</Typography>
        </Box>
      ),
    },
    {
      field: 'email', headerName: 'Email', flex: 1, minWidth: 220,
      renderCell: ({ value }) => (
        <Typography variant="body2" color="#475569">{value}</Typography>
      ),
    },
    {
      field: 'role', headerName: 'Role', width: 170,
      renderCell: () => (
        <Chip
          label="Platform Admin"
          size="small"
          icon={<Shield sx={{ fontSize: '13px !important', color: '#34A8E0 !important' }} />}
          sx={{
            bgcolor: '#EBF6FD', color: '#34A8E0',
            border: '1px solid #B3DCF2', fontSize: 11, fontWeight: 600,
          }}
        />
      ),
    },
    {
      field: 'created_at', headerName: 'Added', width: 150,
      renderCell: ({ value }) => (
        <Typography variant="caption" color="#64748b">
          {value ? format(new Date(value), 'MMM d, yyyy') : '—'}
        </Typography>
      ),
    },
  ]

  return (
    <Box>
      <AdminPageHeader
        eyebrow="Access control"
        title="Platform Admins"
        description={`${admins.length} admin${admins.length !== 1 ? 's' : ''} with unrestricted console access.`}
        icon={<AdminPanelSettings />}
        actions={<Button variant="contained" startIcon={<Add />} onClick={() => setDialogOpen(true)}>Add Admin</Button>}
      />

      {/* Warning banner */}
      <Box sx={{
        display: 'flex', alignItems: 'flex-start', gap: 1.5, p: 1.5, mb: 3,
        borderRadius: 1.5, bgcolor: '#fffbeb', border: '1px solid #fde68a',
      }}>
        <AdminPanelSettings sx={{ color: '#f59e0b', fontSize: 18, mt: 0.125 }} />
        <Typography variant="body2" color="#92400e" sx={{ fontSize: 13 }}>
          Platform admins have unrestricted access to all tenants, billing, and system configuration.
          Only add trusted team members.
        </Typography>
      </Box>

      {createAdmin.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>Failed to create admin — email may already exist</Alert>
      )}
      {adminsQuery.isError && (
        <AdminDataError message="Platform administrators could not be loaded." onRetry={() => { void adminsQuery.refetch() }} />
      )}

      <Card sx={{ bgcolor: '#ffffff', overflow: 'hidden' }}>
        <DataGrid
          rows={admins}
          columns={columns}
          loading={isLoading}
          autoHeight
          pageSizeOptions={[10, 25]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          disableRowSelectionOnClick
          sx={adminGridSx}
          slots={{
            noRowsOverlay: () => (
              <AdminEmptyState compact icon={<Shield />} title="No platform admins" description="Add a trusted owner to grant access to the console." actionLabel="Add admin" onAction={() => setDialogOpen(true)} />
            ),
          }}
        />
      </Card>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 2.5, border: '1px solid #e2e8f0' } }}>
        <DialogTitle sx={{ pb: 1, fontWeight: 700, color: '#0f172a' }}>Add Platform Admin</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField label="Full Name" value={form.name} required fullWidth autoFocus size="small"
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
          <TextField label="Email Address" type="email" value={form.email} required fullWidth size="small"
            onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} />
          <TextField
            label="Password" type={showPassword ? 'text' : 'password'}
            value={form.password} required fullWidth size="small"
            autoComplete="new-password"
            error={form.password !== '' && !isPasswordValid(form.password)}
            helperText={
              (form.password !== '' && passwordError(form.password)) ||
              'At least 10 characters, mixing upper, lower, digits or symbols'
            }
            onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
            InputProps={{
              endAdornment: (
                <Button size="small" onClick={() => setShowPassword((v) => !v)}
                  sx={{ color: '#94a3b8', minWidth: 0, px: 1, fontSize: 11 }}>
                  {showPassword ? 'Hide' : 'Show'}
                </Button>
              ),
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => { setDialogOpen(false); setForm(emptyForm) }} sx={{ color: '#64748b' }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit}
            disabled={createAdmin.isPending || !form.name || !form.email || !isPasswordValid(form.password)}
            sx={{ background: 'linear-gradient(135deg, #34A8E0, #2B8BB9)', borderRadius: 1.5, fontWeight: 600 }}>
            {createAdmin.isPending ? 'Creating…' : 'Create Admin'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
